import { Box, Card, CardContent, CircularProgress, Typography } from "@mui/material";
import { EegDataRecord, EEGMetric, EEGRecording, PairFrequencyBand } from "../types";
import { calculateGlobalMeanZscore, calculateImprovement, calculateExpectedRowsAndMismatch } from "../utils/calculationsUtils";
import { useDataFilter } from "./DataFilter";
import { ComparisonMetricDisplay } from "./ComparisonMetricDisplay";
import { useEegDataForMetric, useAllEegData } from "../utils/useEeegData";

type MetricCardProps = {
    metric: EEGMetric;
    showDataInTable: (metricName: string, rawData: Array<EegDataRecord>) => void;
    firstEEG: EEGRecording;
    secondEEG?: EEGRecording;
    bandFrequencyPairs: Array<PairFrequencyBand>;
    allEegData: ReturnType<typeof useAllEegData>;
};

export const MetricCard = ({ metric, showDataInTable, firstEEG, secondEEG, bandFrequencyPairs, allEegData }: MetricCardProps) => {
    const { selectedFilter } = useDataFilter();

    const { firstEEGData, secondEEGData, firstEEGDataStatus, isFetchingSecondEEGData, secondEEGDataStatus } = useEegDataForMetric(
        metric.id,
        allEegData
    );

    if (firstEEGDataStatus === "loading") return <CircularProgress />;
    if (firstEEGDataStatus === "error") return <Typography>Error loading metric</Typography>;
    if (firstEEGData.length === 0) return null;

    // Calculate expected rows and mismatches using utility function
    const firstMismatchCheck = calculateExpectedRowsAndMismatch(firstEEGData, firstEEG, selectedFilter, bandFrequencyPairs, metric.id);
    const secondMismatchCheck = secondEEG ? calculateExpectedRowsAndMismatch(secondEEGData, secondEEG, selectedFilter, bandFrequencyPairs, metric.id) : { expectedRows: 0, actualRows: 0, hasDataMismatch: false };
    
    const firstEEGMetricMean = calculateGlobalMeanZscore(firstEEGData);
    const thereIsSecondEEGData = secondEEGDataStatus === "success" && secondEEGData.length > 0;
    const secondEEGMetricMean = thereIsSecondEEGData ? calculateGlobalMeanZscore(secondEEGData) : 0;
    const improvement = thereIsSecondEEGData ? calculateImprovement(firstEEGMetricMean, secondEEGMetricMean) : 0;

    return (
        <Card
            sx={{
                textAlign: "center",
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                height: "100%",
                width: "100%",
            }}
        >
            <CardContent>
                <Box display="flex" justifyContent="center" alignItems="center">
                    <Typography variant="subtitle2" color="text.secondary" noWrap>
                        {metric.name_external}
                    </Typography>
                </Box>

                <ComparisonMetricDisplay
                    firstEEGMetricMean={firstEEGMetricMean}
                    secondEEGMetricMean={secondEEGMetricMean}
                    improvement={improvement}
                    onFirstEEGClick={() => showDataInTable(metric.name_external, firstEEGData)}
                    onSecondEEGClick={() => showDataInTable(metric.name_external, secondEEGData)}
                    variant="h4"
                    showComparison={!!secondEEG?.id && thereIsSecondEEGData}
                    secondEEGIsFetching={isFetchingSecondEEGData}
                    rowsMismatchCheck={{
                        first: firstMismatchCheck,
                        second: secondMismatchCheck
                    }}
                />
            </CardContent>
        </Card>
    );
};
